/**
 * Creates tables in SQLite :memory: for entities with adapter: 'memory'.
 * Called at boot time, before seeds.
 */
import type { Knex } from 'knex'
import type { EntityDefinition, FieldDefinition } from '@gzl10/nexus-sdk'
import { getMemoryKnex } from './memory-knex.js'

/**
 * Create tables in the :memory: Knex for all entities with adapter: 'memory'.
 */
export async function createMemoryTables(definitions: EntityDefinition[]): Promise<void> {
  const memKnex = getMemoryKnex()

  for (const def of definitions) {
    const defAny = def as unknown as Record<string, unknown>
    const adapter = defAny['adapter'] as string | undefined
    if (adapter !== 'memory') continue
    const table = defAny['table'] as string | undefined
    if (!table) continue

    if (!await memKnex.schema.hasTable(table)) {
      await createTableFromDefinition(memKnex, def)
    }
  }
}

/**
 * Create a single table from an EntityDefinition using Knex schema builder.
 */
async function createTableFromDefinition(db: Knex, def: EntityDefinition): Promise<void> {
  const defAny = def as unknown as Record<string, unknown>
  const tableName = (defAny['table'] as string)

  await db.schema.createTable(tableName, (table) => {
    // ID column
    const idType = (defAny['idType'] as string) ?? 'ulid'
    if (idType === 'auto') {
      table.increments('id').primary()
    } else {
      table.string('id', 36).primary()
    }

    // Entity fields (skip 'id' — already created as PK above)
    const fieldNames = new Set(Object.keys(def.fields ?? {}))
    for (const [name, field] of Object.entries(def.fields ?? {})) {
      if (name === 'id') continue
      buildColumn(table, db, name, field)
    }

    // Auto-fields: timestamps (skip if already declared in fields)
    if (defAny['timestamps']) {
      if (!fieldNames.has('created_at')) table.timestamp('created_at').defaultTo(db.fn.now())
      if (!fieldNames.has('updated_at')) table.timestamp('updated_at').defaultTo(db.fn.now())
    }

    // Auto-fields: soft delete (skip if already declared in fields)
    if (defAny['softDelete']) {
      if (!fieldNames.has('deleted_at')) table.timestamp('deleted_at').nullable()
    }

    // Auto-fields: audit (skip if already declared in fields)
    if (defAny['audit']) {
      if (!fieldNames.has('created_by')) table.string('created_by').nullable()
      if (!fieldNames.has('updated_by')) table.string('updated_by').nullable()
    }

    // Tree: parent_id with FK
    if (def.type === 'tree') {
      table.string('parent_id', 36).nullable().references('id').inTable(tableName)
    }
  })

  // DAG: create parents junction table
  if (def.type === 'dag') {
    const parentsTable = (defAny['parentsTable'] as string) ?? `${tableName}_parents`
    if (!await db.schema.hasTable(parentsTable)) {
      await db.schema.createTable(parentsTable, (table) => {
        table.string('node_id', 36).notNullable().references('id').inTable(tableName).onDelete('CASCADE')
        table.string('parent_id', 36).notNullable().references('id').inTable(tableName).onDelete('CASCADE')
        table.primary(['node_id', 'parent_id'])
      })
    }
  }
}

/**
 * Build a single column from a FieldDefinition using Knex schema builder.
 */
function buildColumn(table: Knex.CreateTableBuilder, db: Knex, name: string, field: FieldDefinition): void {
  const dbConfig = field.db
  if (!dbConfig) return
  if (dbConfig.virtual) return

  let col: Knex.ColumnBuilder

  switch (dbConfig.type) {
    case 'string':
      col = dbConfig.size ? table.string(name, dbConfig.size) : table.string(name)
      break
    case 'text':
      col = table.text(name)
      break
    case 'integer':
      col = table.integer(name)
      break
    case 'decimal':
      col = dbConfig.precision
        ? table.decimal(name, dbConfig.precision[0], dbConfig.precision[1])
        : table.decimal(name)
      break
    case 'boolean':
      col = table.boolean(name)
      break
    case 'datetime':
      col = table.timestamp(name)
      break
    case 'date':
      col = table.date(name)
      break
    case 'json':
    case 'array':
      col = table.json(name)
      break
    case 'uuid':
      col = table.uuid(name)
      break
    default:
      col = table.specificType(name, String(dbConfig.type))
  }

  // Nullable
  if (dbConfig.nullable === false) {
    col.notNullable()
  } else {
    col.nullable()
  }

  // Defaults
  if (dbConfig.defaultFn === 'now') {
    col.defaultTo(db.fn.now())
  } else if (dbConfig.default !== undefined) {
    col.defaultTo(dbConfig.default as any)
  }

  // Unique
  if (dbConfig.unique) {
    col.unique()
  }
}
